//
// Copyright 2020 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import React from "react";
import {makeStyles} from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import * as Utils from "eai-configurator-ui/components/Utils";
import Button from "@material-ui/core/Button";
import DetailsIcon from "@material-ui/icons/Subject";
import ErrorIcon from "@material-ui/icons/Error";
import DeleteIcon from "@material-ui/icons/Delete";
import InfoIcon from "@material-ui/icons/InfoOutlined";
import IconButton from "@material-ui/core/IconButton";
import TaskDetailsDialog from "src/components/auditor/table/TaskDetailsDialog";
import Tooltip from "@material-ui/core/Tooltip";
import Moment from "moment";
import ErrorMessageDialog, {useErrorMessageDialog,} from "eai-configurator-ui/components/configuration/dialog/ErrorMessageDialog";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  addItem: {
    padding: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  table: {
    minWidth: 500,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

const createTimestampMessage = (timestampMillis) => {
  return Moment(new Date(timestampMillis)).fromNow();
};

export default function AuditorTasksTable({lastFileUploadTimestamp}) {
  const classes = useStyles();
  const [tasks, setTasks] = React.useState([]);
  const [displayDetailsForTask, setDisplayDetailsForTask] = React.useState(null);
  const [errorMessage, setErrorMessage] = useErrorMessageDialog();

  const populateTable = React.useCallback(() => {
    Utils.fetchData("auditor/tasks")
      .then(({tasks}) => {
        setTasks(tasks);
      })
      .catch(function (response) {
        Utils
          .getErrorMessage(response, "Failed to refresh tasks.")
          .then(setErrorMessage);
      });
  }, [setErrorMessage]);

  /**
   * Schedule the table re-population every 3 seconds
   */
  React.useEffect(() => {
      let interval = setInterval(() => {
        populateTable()
      }, 3000)

      return () => {
        clearInterval(interval)
      }
    }, [populateTable]
  )

  React.useEffect(() => {
    populateTable()
    // eslint-disable-next-line
  }, []) // Do not accept React's suggestion to fix this. I want it to be an empty array.

  const removeResults = (task) => {
    const updatedTasks = [...tasks];
    updatedTasks.splice(updatedTasks.indexOf(task), 1);
    setTasks(updatedTasks);

    Utils
      .fetchNonJsonDataUsingNonJson("auditor/remove?id=" + task.id)
      .catch(function (response) {
        Utils
          .getErrorMessage(response, "Request failed.")
          .then(setErrorMessage);
      });
  };

  const isTableEmpty = tasks === undefined || tasks === null || Array.isArray(tasks) === false || tasks.length === 0;

  return (
    <div className={classes.root}>
      <ErrorMessageDialog {...errorMessage} />
      <TaskDetailsDialog
        taskId={displayDetailsForTask}
        handleClosed={setDisplayDetailsForTask}
      />
      <Paper className={classes.paper}>
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size="small"
            aria-label="enhanced table"
          >
            {isTableEmpty && (
              <TableBody>
                <TableRow style={{height: 53}}>
                  <TableCell>
                    <Typography align="center" color="textSecondary">
                      Empty
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}

            {!isTableEmpty && (
              <TableHead>
                <TableRow>
                  <TableCell align="left">Task</TableCell>
                  <TableCell align="center">User</TableCell>
                  <TableCell align="center">Updated</TableCell>
                  <TableCell align="center">Database</TableCell>
                  <TableCell align="center">Message</TableCell>
                  <TableCell align="center">Result</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {tasks.map((task, index) => (
                <TableRow hover role="task" tabIndex={-1} key={index}>
                  <TableCell
                    align="left"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {task.title}
                  </TableCell>
                  <TableCell
                    align="center"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {task.submitter}
                  </TableCell>
                  <Tooltip
                    title={new Date(task.lastUpdateTimestamp).toLocaleString()}
                  >
                    <TableCell
                      align="center"
                      component="th"
                      scope="row"
                      padding="default"
                    >
                      {createTimestampMessage(task.lastUpdateTimestamp)}
                    </TableCell>
                  </Tooltip>
                  <TableCell
                    align="center"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {task.database}
                  </TableCell>
                  <TableCell
                    align="center"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {task.message}
                  </TableCell>
                  <TableCell
                    align="center"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {task.completed ? (
                      <span>
                          <Button
                            variant="contained"
                            color={task.success ? "primary" : "secondary"}
                            className={classes.button}
                            onClick={() => setDisplayDetailsForTask(task.id)}
                            startIcon={
                              task.success ? <DetailsIcon/> : <ErrorIcon/>
                            }
                          >
                            Details
                          </Button>
                        </span>
                    ) : (
                      <span>
                          <CircularProgress/>
                        </span>
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    component="th"
                    scope="row"
                    padding="default"
                  >
                    {!task.completed && (
                      <IconButton onClick={() => setDisplayDetailsForTask(task.id)}>
                        <InfoIcon/>
                      </IconButton>
                    )}
                    <IconButton onClick={() => removeResults(task)}>
                      <DeleteIcon/>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}
